import Grid2 from '@mui/material/Grid2'
import { styled } from '@mui/material/styles'
import React from 'react'

import InputField from '@/app/component/atom/form/input'
import { useCampaignDebouncedState } from '@/app/module/campaigns/component/hooks/use-campaign-debounced-state'
import { ContentFlow, WhatsAppFlow } from '@/app/module/campaigns/types/common'
import TextMessage from '@/app/module/common/whatsapp/text-message'
import { PersonalizationType } from '@/app/types'

import { useWaFlowFieldError } from './hooks/use-wa-flow-field-error'
import WaFlowEditor from './wa-flow-editor'
import { BUTTON_MAX_LENGTH, MESSAGE_MAX_LENGTH } from './wa-flow-editor-utils'
import WaFlowPreview from './wa-flow-preview'

type Props = {
  flow: ContentFlow
  isNewMessage?: boolean
  message?: WhatsAppFlow
  personalizationList: PersonalizationType

  onChange: (payload: Partial<WhatsAppFlow>) => void
}

const WaFlow: React.FC<Props> = ({ flow, isNewMessage = false, message, personalizationList, onChange }) => {
  const handleChange = React.useCallback(
    (updatedFlow: ContentFlow) => {
      if (message) {
        onChange({
          ...message,
          flow: updatedFlow,
        })
        return
      }

      onChange({ flow: updatedFlow })
    },
    [message, onChange],
  )
  const [draftFlow, setDraftFlow] = useCampaignDebouncedState(flow, handleChange)
  const [selectedPageId, setSelectedPageId] = React.useState(flow.pages?.[0]?.id || '')
  const [buttonValidationError, revealButtonError] = useWaFlowFieldError(
    draftFlow.button || '',
    BUTTON_MAX_LENGTH,
    !isNewMessage,
  )
  const [textError, revealTextError] = useWaFlowFieldError(draftFlow.text || '', MESSAGE_MAX_LENGTH, !isNewMessage)
  let buttonError = ''
  if (buttonValidationError) {
    buttonError = draftFlow.button
      ? `Meta limits form button text to ${BUTTON_MAX_LENGTH} characters.`
      : 'Form button text is required.'
  }
  const shouldAutoFocusMessage = isNewMessage && !flow.text

  React.useEffect(() => {
    setSelectedPageId((s) => s || flow.pages?.[0]?.id)
  }, [flow])

  const handleFlowChange = React.useCallback(
    (updatedFlow: ContentFlow) => {
      setDraftFlow(updatedFlow)
    },
    [setDraftFlow],
  )

  const handleButtonInputChange = React.useCallback(
    ({ value }: { name: string; value: string }) => {
      setDraftFlow((currentFlow) => ({
        ...currentFlow,
        button: value,
      }))
    },
    [setDraftFlow],
  )

  const handleTextErrorChange = (error: string) => {
    if (error) {
      revealTextError()
    }
  }

  const handleTextInputChange = React.useCallback(
    ({ text }: { text: string }) => {
      setDraftFlow((currentFlow) => ({
        ...currentFlow,
        text,
      }))
    },
    [setDraftFlow],
  )

  return (
    <WaFlowRoot container spacing={2}>
      <Grid2 size={{ xs: 12 }}>
        <TextMessage
          autoFocus={shouldAutoFocusMessage}
          error={textError}
          info=""
          label="Message"
          personalizationList={personalizationList}
          required
          text={draftFlow.text || ''}
          changeHandler={handleTextInputChange}
          onBlur={revealTextError}
          onErrorChange={handleTextErrorChange}
        />
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <InputField
          error={buttonError}
          label="Flow button text"
          name="flow-button"
          required
          value={draftFlow.button || ''}
          variant="standard"
          onBlur={revealButtonError}
          onChange={handleButtonInputChange}
        />
      </Grid2>
      <TopDivider size={{ xs: 12 }} />
      <Grid2 size={{ xs: 12, md: 8 }}>
        <WaFlowEditor
          flow={draftFlow}
          isNewForm={isNewMessage}
          selectedPageId={selectedPageId}
          onChange={handleFlowChange}
          onPageSelect={setSelectedPageId}
        />
      </Grid2>
      <PreviewColumn size={{ xs: 12, md: 4 }}>
        <WaFlowPreview activePageId={selectedPageId} flow={draftFlow} />
      </PreviewColumn>
    </WaFlowRoot>
  )
}

const WaFlowRoot = styled(Grid2)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(2),
}))

const TopDivider = styled(Grid2)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(2),
}))

const PreviewColumn = styled(Grid2)(({ theme }) => ({
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(2),
  },
  '&::before': {
    backgroundColor: theme.palette.divider,
    bottom: 0,
    content: '""',
    display: 'none',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '1px',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
}))

export default WaFlow

export type { Props as WaFlowProps }
